import React, { useMemo } from 'react'
import { Row, Col, Typography } from 'antd'
import _ from 'lodash'
import { Button, Icon, Popover, PopoverImg } from '@vzmi/shp_shared_compo'
import { CDN } from '@vzmi/shp_shared_compo/dist/constants/cdn.js'
import { Field } from '../../../../../Common/forms'
import {
  SingleLineTextField,
  InputCountField,
  MenuSelectField,
  HyperLink
} from '../../../../../Common/Form'
import ExtraModelTable from '../../ExtraModelTable'
import {
  TABLE_TYPE,
  FORM,
  COLUMN,
  GROUP_SKU_KEY,
  SHORT_TITLE_LIMIT,
  ATTR_NAME_LIMIT,
  DISPLAY_NAME_VALIDATION_TYPE,
  LIMIT,
  getRenderId,
  shortTitleValidationChain,
  displayNameValidationChain,
  attrNameValidationChain,
  customDisplayNameValidationChain,
  getExtraModelsOverLimitInfo
} from '../helper'
import withTableActions from '../withTableActions'
import styles from '../index.css'
import { IconPopoverLabel } from '../../../../Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import { ShortTitleWithPopoverAndSeparator } from '../SelectComplimentaries'

const { Paragraph } = Typography

const tableType = TABLE_TYPE.COMPLIMENTARIES
const renderField = FORM[tableType]

export const ShortTitleWithPopover = () => {
  return (
    <IconPopoverLabel
      popoverPlacement='top'
      className={styles.shortTitle}
      popoverContent={(
        <>
          <p>顯示於 mobile 上之贈品標題</p>
          <PopoverImg
            url={`${CDN}/supplier/shortTitleTip.png`}
            width={664}
            height={342}
          />
        </>
      )}
    >
      贈品短標題
    </IconPopoverLabel>
  )
}

const Complimentaries = props => {
  const {
    change,
    readOnly,
    isDataEmpty,
    isMultipleAttrType,
    isMultipleSpecs,
    title,
    tableData,
    tableDataGroupByGroupSku,
    displayNameOptions,
    groupMapByGroupSku,
    removeRow,
    removeAllRows,
    onFieldChange,
    onSelectCollect,
    onOpenSpecSortModal,
    onOpenNewComplimentariesModal,
    shortTitleFeatureToggle
  } = props
  const isAttrModelExist = !_.isEmpty(displayNameOptions)

  const { isOverLimit, errorMessage } = useMemo(() => {
    return getExtraModelsOverLimitInfo(tableDataGroupByGroupSku, groupMapByGroupSku)
  }, [tableDataGroupByGroupSku, groupMapByGroupSku])

  const columns = [
    {
      title: '商品編號 / 商品名稱',
      width: 200,
      dataIndex: COLUMN.NAME,
      key: COLUMN.NAME,
      render: (text, record, index) => {
        const renderId = getRenderId(record)
        const { [COLUMN.IS_NEW]: isNew } = record
        return (
          <>
            <div>
              {isNew && (
                <>
                  <Icon
                    name='ec-flash'
                    color='#ec457d'
                    align='bottom'
                    size={24}
                  />
                  <span>NEW</span>
                </>
              )}
              <Field
                name={`${renderField}.${renderId}.${COLUMN.ID}`}
                component={SingleLineTextField}
              />
            </div>
            <Paragraph ellipsis={{ rows: 2 }}>
              <Field
                name={`${renderField}.${renderId}.${COLUMN.NAME}`}
                component={SingleLineTextField}
              />
            </Paragraph>
          </>
        )
      }
    },
    {
      title: () => {
        const title = '贈品完整名稱'
        const popoverContent = '顯示於賣場上的贈品名稱'
        // 無屬性、有屬性沒有同時存在
        // 有屬性只有一種
        if (!isMultipleAttrType && !isMultipleSpecs) {
          return (
            <>
              <ShortTitleWithPopoverAndSeparator shortTitleFeatureToggle={shortTitleFeatureToggle} />
              <Popover content={popoverContent}>
                {title}
              </Popover>
            </>
          )
        }

        return (
          <>
            <ShortTitleWithPopoverAndSeparator shortTitleFeatureToggle={shortTitleFeatureToggle} />
            <Popover content={popoverContent}>
              {`${title} `}
            </Popover>
            <Icon
              name='compose'
              align='initial'
              size={14}
              style={{ cursor: 'pointer' }}
              onClick={onOpenSpecSortModal}
            />
          </>
        )
      },
      width: 230,
      dataIndex: COLUMN.DISPLAY_NAME,
      key: COLUMN.DISPLAY_NAME,
      className: styles.verticalAlignTop,
      render: (text, record, index) => {
        const renderId = getRenderId(record)
        const rowId = `${renderField}.${renderId}`
        const shortTitleFieldName = `${rowId}.${COLUMN.SHORT_TITLE}`
        const fieldName = record.isNoAttr ? `${rowId}.${COLUMN.ATTR_NAME}` : `${rowId}.${GROUP_SKU_KEY}`

        return (
          <>
            {
              shortTitleFeatureToggle && (
                <Field
                  name={shortTitleFieldName}
                  component={InputCountField}
                  onChange={onFieldChange}
                  countLimit={SHORT_TITLE_LIMIT}
                  disabled={readOnly}
                  validate={shortTitleValidationChain}
                  alwaysIgnoreError={readOnly}
                  mustShowFormError={!readOnly}
                  showErrorOnActive
                  keepErrorPosition
                />
              )
            }
            {
              record.isNoAttr
                ? (
                  // 無屬性只有一個欄位，直接帶 selectedValue
                  <Field
                    name={fieldName}
                    component={InputCountField}
                    onChange={onFieldChange}
                    countLimit={ATTR_NAME_LIMIT}
                    disabled={readOnly}
                    validate={displayNameValidationChain(
                      tableType,
                      renderId,
                      DISPLAY_NAME_VALIDATION_TYPE.NO_ATTR_INPUT
                    )}
                    alwaysIgnoreError={readOnly}
                    mustShowFormError={!readOnly}
                    showErrorOnActive
                    keepErrorPosition
                  />
                )
                : (
                  <Field
                    name={fieldName}
                    component={MenuSelectField}
                    onChange={onSelectCollect(rowId)}
                    options={displayNameOptions}
                    validate={displayNameValidationChain(
                      tableType,
                      renderId,
                      DISPLAY_NAME_VALIDATION_TYPE.ATTR_SELECT
                    )}
                    customInputValue={v => [v]}
                    customUpdateValue={v => _.get(v, '0')}
                    customInputCountLimit={ATTR_NAME_LIMIT}
                    validateCustomInput={customDisplayNameValidationChain}
                    disabled={readOnly}
                    alwaysIgnoreError={readOnly}
                    enableFilter={false}
                    closeOnSelect
                    closeOnCustomInputSubmit
                    editableCustomInput
                    enableCustomInput
                    enableCustomInputValidation
                    enableCustomInputCounter
                    keepErrorPosition
                    showOptionLabel
                  />
                )
            }
          </>
        )
      }
    },
    {
      title: isAttrModelExist ? '贈品屬性名稱' : '',
      width: 200,
      dataIndex: COLUMN.ATTR_NAME,
      key: COLUMN.ATTR_NAME,
      className: styles.verticalAlignTop,
      render: (text, record, index) => {
        if (record.isNoAttr) {
          return null
        }

        const renderId = getRenderId(record)
        const rowId = `${renderField}.${renderId}`
        return (
          <Field
            name={`${rowId}.${COLUMN.ATTR_NAME}`}
            component={InputCountField}
            onChange={onFieldChange}
            countLimit={ATTR_NAME_LIMIT}
            disabled={readOnly}
            validate={attrNameValidationChain(tableType, renderId)}
            alwaysIgnoreError={readOnly}
            mustShowFormError={!readOnly}
            showErrorOnActive
            keepErrorPosition
          />
        )
      }
    },
    {
      title: '可售數量',
      width: 90,
      dataIndex: COLUMN.STOCK,
      key: COLUMN.STOCK,
      align: 'right',
      render: (text, record, index) => {
        const proudctCount = _.get(record, COLUMN.AVAILABLE_COUNT, _.get(record, COLUMN.STOCK, null))
        return +proudctCount === 0 ? null : proudctCount
      }
    },
    {
      title: '供應商商品料號',
      width: 120,
      dataIndex: COLUMN.PART_NO,
      key: COLUMN.PART_NO,
      render: (text, record, index) => {
        const renderId = getRenderId(record)
        return (
          <Field
            name={`${renderField}.${renderId}.${COLUMN.PART_NO}`}
            component={SingleLineTextField}
            defaultEmptyString
          />
        )
      }
    }
  ]
  return (
    <>
      <Row
        wrap={false}
        align='middle'
      >
        <Col flex='20%'>
          <div>
            {`最多 ${LIMIT.GROUP} X ${LIMIT.RECORDS} 組`}
            {title}
            {
              isOverLimit && (
                <div className='has-form-error'>
                  {errorMessage}
                </div>
              )
            }
          </div>
        </Col>
        <Col flex='auto' />
        <Col flex='20%'>
          <Row justify='end'>
            <Col>
              {!readOnly && (
                <Button
                  size='medium'
                  buttonType='outline'
                  data-role='remove-all-extra-models'
                  onClick={removeAllRows}
                  disabled={isDataEmpty}
                  fluid
                >
                  清空
                  {title}
                </Button>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      <ExtraModelTable
        tableType={tableType}
        tableData={tableData}
        columns={columns}
        isMultipleAttrType={isMultipleAttrType}
        removeRow={removeRow}
        readOnly={readOnly}
        change={change}
      />
      {
        !readOnly && (
          <>
            <br />
            <Row>
              <Col flex='auto'>
                <HyperLink
                  data-role='import-extra-models'
                  onClick={onOpenNewComplimentariesModal}
                >
                  <Icon
                    name='add-attachments'
                    color='#5478fc'
                    size={14}
                    align='middle'
                    style={{ cursor: 'pointer' }}
                  />
                  {` ${`匯入${title}`}`}
                </HyperLink>
              </Col>
            </Row>
          </>
        )
      }
    </>
  )
}

const props = { tableType }
export default withTableActions(Complimentaries, props)
